/**
 * 指挥页底图坐标系适配（对外入口）
 *
 * 业务数据（队员/轨迹/救援/令牌选点）统一按 WGS84 存储。
 * 显示纠偏只看当前底图在 mapDatumRegistry 里的能力，不要在功能里再写一套。
 *
 * 统一入口（推荐，新代码一律用）：
 *   上图：toDisplay(map, lng, lat) / toDisplayGeometry(map, geom) / toDisplayGeoJSON(map, geojson)
 *   回写：fromDisplay(map, lng, lat)（点击、拖拽、绘制、框选、getCenter 得到的坐标，存库/发送前必须调用）
 *   独立页面地图（推送/选点/坐标工具/首页）：创建后 attachCoordAdapter(map, { datum: DATUM_WGS84 })，
 *   不再沿用指挥页的全局底图状态。
 * 旧接口（保留，行为不变）：toMapLngLat / fromMapLngLat / toMapGeometry / toMapGeoJSON
 * DEM：needsDemWarp（true 则换纠偏高程源）
 */

import {
  createCoordAdapter,
  MAP_DATUM_EVENT,
  DATUM_GCJ02,
  DATUM_WGS84
} from './mapDatumRegistry';

export {
  DATUM_PROFILES,
  BASEMAP_DATUM_RULES,
  DATUM_WGS84,
  DATUM_GCJ02,
  DATUM_BD09,
  MAP_DATUM_EVENT,
  getDatumProfile,
  resolveBasemapDatum,
  registerBasemapDatums,
  createCoordAdapter,
  transformGeometry,
  transformGeoJSON
} from './mapDatumRegistry';

/** @type {string} */
let activeBasemapId = 'mapbox-satellite';
/** @type {{ provider?: string, datum?: string }} */
let activeMeta = {};

function attachAdapter(map, adapter) {
  try {
    if (!map) return;
    map.__zhxtBasemapId = adapter.basemapId || activeBasemapId;
    map.__zhxtCoordAdapter = adapter;
    map.__zhxtMapDatum = adapter.datum;
  } catch (_) {
    /* ignore */
  }
  try {
    map?.fire?.(MAP_DATUM_EVENT, {
      datum: adapter.datum,
      basemapId: adapter.basemapId || activeBasemapId,
      warpDem: adapter.warpDem,
      warpDisplay: adapter.warpDisplay
    });
  } catch (_) {
    /* 无 fire 的测试替身忽略 */
  }
}

export function setActiveBasemapIdForCoord(basemapId, map = null, meta = null) {
  if (basemapId) activeBasemapId = String(basemapId);
  if (meta && typeof meta === 'object') {
    activeMeta = {
      provider: meta.provider || undefined,
      datum: meta.datum || undefined
    };
  } else {
    activeMeta = {};
  }
  const adapter = createCoordAdapter(activeBasemapId, activeMeta);
  attachAdapter(map, adapter);
  return adapter;
}

/**
 * 当前底图 id：优先模块状态（与 React currentBasemapId 同步），
 * 勿优先读 map.__zhxtBasemapId（切图中途可能短暂过期，导致 Mapbox/Esri 误做 GCJ 纠偏）。
 */
export function getActiveBasemapIdForCoord(map = null) {
  if (activeBasemapId) return activeBasemapId;
  try {
    if (map?.__zhxtBasemapId) return String(map.__zhxtBasemapId);
  } catch (_) {
    /* ignore */
  }
  return 'mapbox-satellite';
}

/** 当前底图的纠偏适配器（点/面/DEM 都读这里） */
export function getMapCoordAdapter(map = null, basemapId = null) {
  const id = basemapId || getActiveBasemapIdForCoord(map);
  try {
    const attached = map?.__zhxtCoordAdapter;
    // 独立页面地图用 attachCoordAdapter 声明了固定基准：不读指挥页全局底图
    if (!basemapId && attached && attached.datum && map.__zhxtFixedDatum) {
      return attached;
    }
    if (
      attached
      && attached.datum
      && (!basemapId || attached.basemapId === String(basemapId))
      && attached.basemapId === String(id)
    ) {
      return attached;
    }
  } catch (_) {
    /* 重建 */
  }
  return createCoordAdapter(id, activeMeta);
}

/** 底图瓦片采用的大地基准 */
export function getBasemapDatum(basemapId = activeBasemapId, map = null) {
  return getMapCoordAdapter(map, basemapId).datum;
}

/** 点/线/面/标记是否需要显示纠偏 */
export function needsDisplayWarp(basemapId = activeBasemapId, map = null) {
  return getMapCoordAdapter(map, basemapId).warpDisplay;
}

/** 全球 DEM 是否需要按显示坐标重采样 */
export function needsDemWarp(basemapId = activeBasemapId, map = null) {
  return getMapCoordAdapter(map, basemapId).warpDem;
}

/** @deprecated 请用 needsDisplayWarp；保留以免旧调用处报错 */
export function needsGcjDisplayCorrection(basemapId = activeBasemapId, map = null) {
  return needsDisplayWarp(basemapId, map);
}

/** 显示坐标在两种基准间转换（用于切图时保持视野对准同一地物） */
export function convertDisplayLngLat(lng, lat, fromDatum, toDatum) {
  const lngN = Number(lng);
  const latN = Number(lat);
  if (!Number.isFinite(lngN) || !Number.isFinite(latN)) return null;
  if (!fromDatum || !toDatum || fromDatum === toDatum) return [lngN, latN];
  const fromAdapter = createCoordAdapter('', { datum: fromDatum });
  const toAdapter = createCoordAdapter('', { datum: toDatum });
  const wgs = fromAdapter.fromDisplayLngLat(lngN, latN) || [lngN, latN];
  return toAdapter.toDisplayLngLat(wgs[0], wgs[1]) || wgs;
}

function resolveBasemapId(basemapId, map) {
  if (basemapId) return String(basemapId);
  return getActiveBasemapIdForCoord(map);
}

/** 业务 WGS84 → 当前底图显示坐标 */
export function toMapLngLat(lng, lat, basemapId, map = null) {
  const adapter = getMapCoordAdapter(map, resolveBasemapId(basemapId, map));
  return adapter.toDisplayLngLat(lng, lat);
}

/** 地图点击/显示坐标 → 业务 WGS84（写入数据库前调用） */
export function fromMapLngLat(lng, lat, basemapId, map = null) {
  const adapter = getMapCoordAdapter(map, resolveBasemapId(basemapId, map));
  return adapter.fromDisplayLngLat(lng, lat);
}

export function toMapLngLatObj(lng, lat, basemapId, map = null) {
  const pair = toMapLngLat(lng, lat, basemapId, map);
  if (!pair) return null;
  return { lng: pair[0], lat: pair[1] };
}

export function fromMapLngLatObj(lng, lat, basemapId, map = null) {
  const pair = fromMapLngLat(lng, lat, basemapId, map);
  if (!pair) return null;
  return { lng: pair[0], lat: pair[1] };
}

/** 业务 WGS84 几何 → 当前底图显示几何 */
export function toMapGeometry(geom, basemapId, map = null) {
  const adapter = getMapCoordAdapter(map, resolveBasemapId(basemapId, map));
  return adapter.toDisplayGeometry(geom);
}

/** 业务 WGS84 GeoJSON → 当前底图显示 GeoJSON */
export function toMapGeoJSON(geojson, basemapId, map = null) {
  const adapter = getMapCoordAdapter(map, resolveBasemapId(basemapId, map));
  return adapter.toDisplayGeoJSON(geojson);
}

export function isGcjDisplayDatum(datum) {
  return String(datum || '') === DATUM_GCJ02;
}

/* ===================== 统一入口（P2） ===================== */

/**
 * 给“独立页面地图”声明固定显示基准（不跟指挥页全局底图走）。
 * @param {object} map mapbox 地图实例
 * @param {{ datum?: string, basemapId?: string }} [options] datum 默认 WGS84
 * @returns 适配器
 */
export function attachCoordAdapter(map, options = {}) {
  const datum = options.datum || DATUM_WGS84;
  const basemapId = String(options.basemapId || `fixed-${String(datum).toLowerCase()}`);
  const adapter = createCoordAdapter(basemapId, { datum });
  if (!map) return adapter;
  try {
    map.__zhxtFixedDatum = true;
  } catch (_) {
    /* ignore */
  }
  attachAdapter(map, adapter);
  return adapter;
}

/** 地图当前显示基准（WGS84 / GCJ02 / BD09） */
export function getMapDatum(map = null) {
  return getMapCoordAdapter(map).datum;
}

/** 业务 WGS84 → 该地图显示坐标 [lng, lat]（非法输入返回 null） */
export function toDisplay(map, lng, lat) {
  return getMapCoordAdapter(map).toDisplayLngLat(lng, lat);
}

/** 该地图显示坐标 → 业务 WGS84 [lng, lat]（非法输入返回 null） */
export function fromDisplay(map, lng, lat) {
  return getMapCoordAdapter(map).fromDisplayLngLat(lng, lat);
}

/** 业务 WGS84 几何 → 该地图显示几何 */
export function toDisplayGeometry(map, geom) {
  return getMapCoordAdapter(map).toDisplayGeometry(geom);
}

/** 业务 WGS84 GeoJSON → 该地图显示 GeoJSON */
export function toDisplayGeoJSON(map, geojson) {
  return getMapCoordAdapter(map).toDisplayGeoJSON(geojson);
}
